import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShieldCheck, Menu, LogOut, LayoutDashboard, UserCircle2 } from "lucide-react";
import { useAuth } from "@/AuthContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const LOGO_URL = "/strategic-tracking-logo.png";

export function TopNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const NavLinks = ({ onNav }) => (
    <>
      <NavLink to="/solutions" onClick={onNav} data-testid="nav-solutions"
        className={({isActive}) => `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>
        Solutions
      </NavLink>
      <NavLink to="/compare" onClick={onNav} data-testid="nav-compare"
        className={({isActive}) => `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>
        Compare
      </NavLink>
      <NavLink to="/request-quotes" onClick={onNav} data-testid="nav-quotes"
        className={({isActive}) => `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>
        Request Quotes
      </NavLink>
      <NavLink to="/pricing" onClick={onNav} data-testid="nav-pricing"
        className={({isActive}) => `text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`}>
        List Your Company
      </NavLink>
    </>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-logo" className="flex items-center gap-3 group">
          <img src={LOGO_URL} alt="Strategic Tracking" className="h-9 w-auto" />
          <div className="leading-tight hidden sm:block border-l border-slate-200 pl-3">
            <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500">Tracking Solution Directory</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLinks />
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {!user && (
            <>
              <Link to="/login" data-testid="nav-login" className="text-sm font-medium text-slate-700 hover:text-slate-900">Login</Link>
              <Link to="/solutions" data-testid="nav-search-cta" className="btn-primary">
                <Search size={16}/> Search Solutions
              </Link>
            </>
          )}
          {user && (
            <>
              {user.role === 'admin' && (
                <Link to="/admin" data-testid="nav-admin" className="btn-secondary"><ShieldCheck size={16}/> Admin</Link>
              )}
              {user.role === 'provider' && (
                <Link to="/provider/dashboard" data-testid="nav-provider-dash" className="btn-secondary"><LayoutDashboard size={16}/> Dashboard</Link>
              )}
              <button data-testid="nav-logout" onClick={async ()=>{ await logout(); navigate('/'); }}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5">
                <LogOut size={15}/> Logout
              </button>
            </>
          )}
        </div>

        <div className="md:hidden">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-mobile-toggle" className="p-2 -mr-2"><Menu size={22}/></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex flex-col gap-6 pt-8">
                <NavLinks onNav={()=>setMobileOpen(false)} />
                <div className="border-t border-slate-200 pt-6 flex flex-col gap-3">
                  {!user ? (
                    <Link to="/login" onClick={()=>setMobileOpen(false)} className="btn-secondary justify-center"><UserCircle2 size={16}/> Login</Link>
                  ) : (
                    <>
                      {user.role === 'admin' && <Link to="/admin" onClick={()=>setMobileOpen(false)} className="btn-secondary justify-center">Admin</Link>}
                      {user.role === 'provider' && <Link to="/provider/dashboard" onClick={()=>setMobileOpen(false)} className="btn-secondary justify-center">Dashboard</Link>}
                      <button onClick={async ()=>{ await logout(); setMobileOpen(false); navigate('/'); }} className="btn-secondary justify-center">Logout</button>
                    </>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50 mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Strategic Tracking" className="h-9 w-auto" />
          </div>
          <p className="mt-3 text-sm text-slate-600 max-w-sm">
            The specialist global directory for tracking devices, condition monitoring,
            shipment visibility, and asset tracking technology providers.
          </p>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Buyers</div>
          <ul className="space-y-2 text-sm text-slate-700">
            <li><Link to="/solutions">Search solutions</Link></li>
            <li><Link to="/request-quotes">Request quotes</Link></li>
            <li><Link to="/compare">Compare providers</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Providers</div>
          <ul className="space-y-2 text-sm text-slate-700">
            <li><Link to="/pricing">Pricing</Link></li>
            <li><Link to="/list-your-company">List your company</Link></li>
            <li><Link to="/login">Provider login</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Categories</div>
          <ul className="space-y-2 text-sm text-slate-700">
            <li><Link to="/tracking-solutions/cold-chain-monitoring">Cold chain</Link></li>
            <li><Link to="/tracking-solutions/asset-tracking">Asset tracking</Link></li>
            <li><Link to="/tracking-solutions/shipment-tracking">Shipment tracking</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-slate-500 flex items-center justify-between">
          <div>© {new Date().getFullYear()} Strategic Tracking. All rights reserved.</div>
          <div className="font-mono">v1.0 · Procurement-grade directory</div>
        </div>
      </div>
    </footer>
  );
}
